import {
	IconBolt,
	IconBroadcast,
	IconCalendar,
	IconClipboardText,
	IconClock,
	IconCloud,
	IconHeartbeat,
	IconKey,
	IconLink,
	IconNumber123,
	IconToggleRight,
	IconUser,
	IconVersions,
} from "@tabler/icons-react";
import { Tooltip, Typography } from "antd";
import Link from "antd/es/typography/Link";
import { format } from "date-fns";
import { useDeploymentOptions } from "@/common/artemis-operations/agents";
import { useRouteOrgSlug } from "@/common/artemis-operations/organization";
import type { Agent, AgentStatus } from "@/common/model";
import { COPYABLE, IdField } from "../base";
import {
	BaseDetails,
	EditableText,
	InlineSelect,
	toOptions,
} from "../base-details";
import { CustomDrawer } from "../layout/workspace/custom-drawer";
import { OwnerField } from "../users/owner-field";
import {
	AgentHealthBadge,
	AgentKindLabelMap,
	AgentStatusBadge,
	AgentStatusLabelMap,
} from "./agent-badges";

interface AgentDetailsProps {
	agent: NonNullable<Agent>;
	onSaveName?: (value: string) => void;
	/** Sets the customer-controlled status (Enabled/Disabled). When omitted, status is read-only. */
	onSaveStatus?: (status: AgentStatus) => void;
	/** Switches the agent's runtime version. When omitted, the version is read-only. */
	onSaveRuntimeVersion?: (version: string) => void;
	/** Hands ownership to another workspace member. When omitted, the owner is read-only. */
	onSaveOwner?: (userId: string) => void;
}

/** Read-only or editable detail view for a single agent. Fields without an onSave are read-only. */
export function AgentDetails({
	agent,
	onSaveName,
	onSaveStatus,
	onSaveRuntimeVersion,
	onSaveOwner,
}: AgentDetailsProps) {
	const isExternal = agent.kind === "EXTERNAL";
	const { data: deployOpts } = useDeploymentOptions();
	const orgSlug = useRouteOrgSlug();
	const availableVersions = deployOpts?.runtimeVersions ?? [];

	return (
		<BaseDetails
			item={agent}
			fields={[
				{
					key: "id",
					tooltip: "Id",
					icon: IconNumber123,
					render: (id) => <IdField id={id} showCopyButton />,
				},
				{
					key: "name",
					tooltip: "Name",
					icon: IconClipboardText,
					render: (name) => (
						<EditableText onChange={onSaveName}>{name as string}</EditableText>
					),
				},
				{
					tooltip: "Status",
					key: "status",
					icon: IconToggleRight,
					render: (status) => (
						<InlineSelect
							value={status as AgentStatus}
							options={toOptions(AgentStatusLabelMap)}
							onChange={onSaveStatus}
						>
							<AgentStatusBadge status={status as AgentStatus} />
						</InlineSelect>
					),
				},
				{
					tooltip: "Kind",
					key: "kind",
					icon: IconCloud,
					// Attributes render as plain text; tags are reserved for state (Status, Reachability).
					render: (kind) => (kind ? AgentKindLabelMap[kind] : "-"),
				},
				{
					tooltip: "Environment",
					label: "Environment",
					key: "environment",
					icon: IconCloud,
					// Null environment = the agent is not running on any tracked environment
					// (a local machine, a remote A2A host, ...) — see ARCH-030. For managed
					// agents a null here historically meant platform-hosted.
					render: (env) =>
						env ? (
							<Link
								href={`/${orgSlug}/workspaces/${agent.workspaceId}/environments/${env.id}`}
							>
								{env.name}
							</Link>
						) : isExternal ? (
							"Self-managed"
						) : (
							"Platform-managed"
						),
				},
				{
					tooltip: "Client ID",
					label: "Client ID",
					key: "clientId",
					icon: IconKey,
					hidden: () => !isExternal,
					render: (clientId) => (
						<Typography.Text
							code
							copyable={{ ...COPYABLE, text: clientId as string }}
						>
							{clientId as string}
						</Typography.Text>
					),
				},
				{
					tooltip: "When the client secret expires",
					label: "Secret expiry",
					key: "clientSecretExpiresAt",
					icon: IconClock,
					hidden: () => !isExternal,
					render: (expiresAt) =>
						expiresAt
							? format(new Date(expiresAt as string), "MM/dd/yyyy h:mm a")
							: "Never",
				},
				{
					tooltip: "Last time the agent authenticated (its liveness heartbeat)",
					label: "Last seen",
					key: "lastSeenAt",
					icon: IconHeartbeat,
					hidden: () => !isExternal,
					render: (lastSeenAt) =>
						lastSeenAt
							? format(new Date(lastSeenAt as string), "MM/dd/yyyy h:mm a")
							: "Never",
				},
				{
					tooltip: "Runtime Version",
					label: "Runtime Version",
					key: "runtimeVersion",
					icon: IconVersions,
					// Hide entirely for External agents (no runtime), and when versioning is off
					// (no configured versions) and the agent isn't pinned to one.
					hidden: (version) =>
						isExternal || (!version && availableVersions.length === 0),
					render: (version) => {
						const current = (version as string | null) ?? undefined;
						// Show current + offered: keep the pinned version selectable even if it's
						// been retired from config, so the current value always renders.
						const options = Array.from(
							new Set([...(current ? [current] : []), ...availableVersions]),
						).map((v) => ({ value: v, label: v }));
						return (
							<InlineSelect
								value={current ?? ""}
								options={options}
								onChange={onSaveRuntimeVersion}
							>
								{current ?? "-"}
							</InlineSelect>
						);
					},
				},
				// {
				//   key: "role",
				//   icon: IconCircleCheck,
				//   render: (role) => <AgentRoleBadge status={role} />,
				// },
				{
					tooltip: "A2A Url",
					key: "endpointUrl",
					icon: IconLink,
					hidden: () => isExternal,
					render: (link) => (
						<Typography.Text
							ellipsis={{ tooltip: link }}
							style={{ maxWidth: 400 }}
							copyable={{ ...COPYABLE, text: link.trim() }}
						>
							{link.trim()}
						</Typography.Text>
					),
				},

				{
					tooltip: "A2A capabilities",
					key: "supportsStreaming",
					icon: IconBroadcast,
					hidden: () => isExternal,
					render: (supportsStreaming, item) =>
						[
							supportsStreaming && "Streaming",
							item.supportsPushNotifications && "Push notifications",
						]
							.filter(Boolean)
							.join(", ") || "-",
				},

				{
					label: "Skills",
					tooltip: "Skills discovered from the agent's A2A card",
					key: "skills",
					icon: IconBolt,
					render: (skills) =>
						skills.length > 0 ? (
							<span>
								{skills.map((skill, i) => (
									<span key={skill.id}>
										{i > 0 && ", "}
										<Tooltip title={skill.description}>{skill.name}</Tooltip>
									</span>
								))}
							</span>
						) : (
							"-"
						),
				},
				{
					tooltip: "Reachability",
					key: "health",
					icon: IconHeartbeat,
					// The platform cannot reach an External agent (no A2A endpoint), so it has no
					// reachability to show; "Last seen" above is its only liveness signal.
					hidden: () => isExternal,
					render: (health) =>
						health ? <AgentHealthBadge status={health} /> : "-",
				},
				{
					tooltip: "Owner",
					key: "ownerId",
					icon: IconUser,
					render: (id) => (
						<OwnerField
							ownerId={id}
							workspaceId={agent.workspaceId}
							onSave={onSaveOwner}
						/>
					),
				},
				{
					key: "createdAt",
					tooltip: "Created",
					icon: IconCalendar,
					render: (createdAt) =>
						format(new Date(createdAt as string), "MM/dd/yyyy h:mm a"),
				},
			]}
		/>
	);
}

export function AgentDetailsDrawer({
	agent,
	open,
	onClose,
	onSaveName,
	onSaveStatus,
	onSaveRuntimeVersion,
}: {
	agent?: NonNullable<Agent>;
	open: boolean;
	onClose: () => void;
	onSaveName?: (value: string) => void;
	onSaveStatus?: (status: AgentStatus) => void;
	onSaveRuntimeVersion?: (version: string) => void;
}) {
	if (!agent) return null;
	return (
		<CustomDrawer title={agent.name} open={open} onClose={onClose}>
			<AgentDetails
				agent={agent}
				onSaveName={onSaveName}
				onSaveStatus={onSaveStatus}
				onSaveRuntimeVersion={onSaveRuntimeVersion}
			/>
		</CustomDrawer>
	);
}
